import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../..';
import {
  IChatConnection,
  IChatNotification,
  IConversation,
  IConversationUser,
  IMessage,
  TMessageStatus,
} from '../ChatSlice';
import { ICourse } from '../CoursesSlice';
import { getAuthUser } from '../../AuthSlice/AuthApi';

export const ADMIN_CHAT_VIEW_MODES = {
  CONVERSATION_LIST: 'CONVERSATION_LIST',
  CONVERSATION: 'CONVERSATION',
  NEW_CONVERSATION: 'NEW_CONVERSATION',
} as const;

export type TAdminChatViewMode = keyof typeof ADMIN_CHAT_VIEW_MODES;

export interface IInitialAdminChatState {
  authUserId?: string;
  isConnected: boolean;
  courseId?: string;
  viewMode?: TAdminChatViewMode;
  selectedConversation?: IConversation;
  adminChatConversationsData: {
    isCreating: boolean;
    isLoading: boolean;
    list: IConversation[];
  };
  adminChatConversationMessagesData: {
    isLoading: boolean;
    listData?: {
      messages: IMessage[];
      limit: number;
      offset: number;
      totalDocs: number;
    };
  };
  adminChatNotificationData: {
    isLoadingMore?: boolean;
    unSeenCount: number;
    notificationListData?: {
      limit?: number;
      offset?: number;
      totalDocs?: number;
      list: Array<IChatNotification>;
    };
  };
}

const initialState: IInitialAdminChatState = {
  authUserId: undefined,
  isConnected: false,
  viewMode: ADMIN_CHAT_VIEW_MODES.CONVERSATION_LIST,
  selectedConversation: undefined,
  adminChatConversationsData: {
    isCreating: false,
    isLoading: false,
    list: [],
  },
  adminChatConversationMessagesData: {
    isLoading: false,
    listData: undefined,
  },
  adminChatNotificationData: {
    unSeenCount: 0,
    notificationListData: undefined,
  },
};

const chatMiddlewareRequestActions = {
  // Conversations
  adminChatGetConversations: (
    state: IInitialAdminChatState,
    action: PayloadAction<{ courseId: string }>
  ) => {
    state.courseId = action.payload.courseId;
    state.adminChatConversationsData = {
      ...state.adminChatConversationsData,
      isLoading: true,
    };
  },
  adminChatStartNewConversation: (
    state: IInitialAdminChatState,
    action: PayloadAction<{ courseId: string; userIds }>
  ) => {
    state.adminChatConversationsData = {
      ...state.adminChatConversationsData,
      isCreating: true,
    };
  },
  // Messages
  adminChatGetConversationMessages: (
    state: IInitialAdminChatState,
    action: PayloadAction<{
      conversationId: string;
      limit: number;
      offset: number;
    }>
  ) => {
    state.adminChatConversationMessagesData = {
      ...state.adminChatConversationMessagesData,
      isLoading: true,
    };
  },
  adminChatSendConversationMessage: (
    state: IInitialAdminChatState,
    action: PayloadAction<{
      conversationId: string;
      message: string;
    }>
  ) => {
    // On Action Triggered
  },
  adminChatSyncConversationMessages: (
    state: IInitialAdminChatState,
    action: PayloadAction<{ messageIds: string[]; status: TMessageStatus }>
  ) => {
    // On Action Triggered
  },
  // NotificationData
  adminChatLoadMoreNotificationData: (
    state: IInitialAdminChatState,
    action: PayloadAction<{ limit: number; offset: number }>
  ) => {
    // On Action Triggered
    if (state.adminChatNotificationData) {
      state.adminChatNotificationData = {
        ...state.adminChatNotificationData,
        isLoadingMore: true,
      };
    }
  },
};

const chatMiddlewareResultSetActions = {
  adminChatSetConnection: (
    state: IInitialAdminChatState,
    action: PayloadAction<boolean>
  ) => {
    state.isConnected = action.payload;
  },
  onAdminChatDisconnected: (state: IInitialAdminChatState) => {
    state = {
      ...initialState,
    };
  },
  onAdminChatUserConnectionSynced: (
    state: IInitialAdminChatState,
    action: PayloadAction<IChatConnection>
  ) => {
    const userConnection = action.payload;

    const newAdminChatConversationsData = {
      ...state.adminChatConversationsData,
    };

    if (newAdminChatConversationsData.list.length) {
      const newList = [...newAdminChatConversationsData.list].map(
        (conversationItem) => {
          const conversationUsers = (conversationItem.users as IConversationUser[]).map(
            (conversationUserItem) => {
              if (conversationUserItem._id === userConnection.user) {
                return {
                  ...conversationUserItem,
                  isOnline: userConnection.isOnline,
                  lastSeen: userConnection.lastSeen,
                };
              }

              return conversationUserItem;
            }
          );

          return {
            ...conversationItem,
            users: conversationUsers,
          };
        }
      );

      newAdminChatConversationsData.list = newList;
    }

    state.adminChatConversationsData = newAdminChatConversationsData;
  },
  // Conversations
  setAdminChatConversations: (
    state: IInitialAdminChatState,
    action: PayloadAction<IConversation[]>
  ) => {
    state.adminChatConversationsData = {
      isCreating: false,
      isLoading: false,
      list: action.payload,
    };
  },
  setAdminChatNewConversation: (
    state: IInitialAdminChatState,
    action: PayloadAction<IConversation>
  ) => {
    const newConversation = action.payload;
    const newList = [...state.adminChatConversationsData.list];
    const isExists = newList.some((item) => item._id === newConversation._id);

    if (!isExists) {
      newList.push({
        ...newConversation,
        unSeenCount: 0,
      });
    }

    state.adminChatConversationsData = {
      ...state.adminChatConversationsData,
      isCreating: false,
      list: newList,
    };

    state.selectedConversation = {
      ...newConversation,
    };

    state.viewMode = ADMIN_CHAT_VIEW_MODES.CONVERSATION;
  },
  // Messages
  setAdminChatConversationMessages: (
    state: IInitialAdminChatState,
    action: PayloadAction<{
      messages: IMessage[];
      limit: number;
      offset: number;
      totalDocs: number;
    }>
  ) => {
    state.adminChatConversationMessagesData = {
      isLoading: false,
      listData: action.payload,
    };
  },
  onAdminChatConversationMessageReceived: (
    state: IInitialAdminChatState,
    action: PayloadAction<IMessage>
  ) => {
    const message = action.payload;
    const messageSender = message.sender;
    const messageConversation = message.conversation as IConversation;
    const conversationCourse = messageConversation.course as ICourse;
    const conversationUsers = messageConversation.users as IConversationUser[];

    // Check Conversation add push messages
    const newAdminChatConversationMessagesData = {
      ...state.adminChatConversationMessagesData,
    };

    if (
      state.viewMode === ADMIN_CHAT_VIEW_MODES.CONVERSATION &&
      state.selectedConversation &&
      messageConversation &&
      state.selectedConversation._id === messageConversation._id &&
      newAdminChatConversationMessagesData.listData
    ) {
      const newListData = { ...newAdminChatConversationMessagesData.listData };

      newListData.totalDocs += 1;
      newListData.messages = [...newListData.messages, message];

      newAdminChatConversationMessagesData.listData = newListData;
    }

    state.adminChatConversationMessagesData = newAdminChatConversationMessagesData;

    // Update conversation data
    if (conversationCourse._id === state.courseId) {
      const newAdminChatConversationsData = {
        ...state.adminChatConversationsData,
      };

      const newConversationList = [...newAdminChatConversationsData.list];

      if (state.authUserId !== messageSender._id) {
        if (newConversationList.length) {
          const index = newConversationList.findIndex(
            (item) => item._id === messageConversation._id
          );

          const matchingItem = { ...newConversationList[index] };

          matchingItem.unSeenCount = (matchingItem.unSeenCount || 0) + 1;
          matchingItem.lastMessageTime = message.createdAt;

          newConversationList[index] = { ...matchingItem };
        }

        const isExist = newConversationList.some(
          (item) => item._id === messageConversation._id
        );

        if (!isExist) {
          const newConversation: IConversation = {
            _id: messageConversation._id,
            course: conversationCourse._id,
            users: [...conversationUsers],
            lastMessageTime: messageConversation.lastMessageTime,
            unSeenCount: 0,
          };

          if (state.authUserId !== messageSender._id) {
            newConversation.unSeenCount = 1;
          }

          newConversationList.push(newConversation);
        }
      } else if (newConversationList.length) {
        const index = newConversationList.findIndex(
          (item) => item._id === messageConversation._id
        );

        if (index !== -1) {
          newConversationList[index] = {
            ...newConversationList[index],
            lastMessageTime: message.createdAt,
          };
        }
      }

      newAdminChatConversationsData.list = newConversationList;

      state.adminChatConversationsData = newAdminChatConversationsData;
    }

    // Update notification data
    const newAdminChatNotificationData = { ...state.adminChatNotificationData };

    if (newAdminChatNotificationData.notificationListData) {
      const newList = [
        ...(newAdminChatNotificationData.notificationListData.list || []),
      ];

      const index = newList.findIndex(
        (item) => item._id === messageConversation._id
      );

      if (index > -1) {
        const matchingItem = { ...newList[index] };

        let unseenCount = matchingItem.unseenCount || 0;

        if (state.authUserId !== messageSender._id) {
          unseenCount += 1;
          newAdminChatNotificationData.unSeenCount += 1;
        }

        newList[index] = {
          ...matchingItem,
          unseenCount,
          lastMessageSentAt: message.createdAt,
          lastMessage: {
            _id: message._id,
            message: message.message,
            sender: {
              _id: messageSender._id,
              firstname: messageSender.firstname,
              lastname: messageSender.lastname,
              avatar: messageSender.avatar,
            },
          },
        };
      } else {
        const newNotification = {
          _id: messageConversation._id,
          unseenCount: 0,
          course: { ...conversationCourse },
          lastMessageSentAt: message.createdAt,
          lastMessage: {
            _id: message._id,
            message: message.message,
            sender: {
              _id: messageSender._id,
              firstname: messageSender.firstname,
              lastname: messageSender.lastname,
              avatar: messageSender.avatar,
            },
          },
        };

        if (state.authUserId !== messageSender._id) {
          newNotification.unseenCount = 1;
          newAdminChatNotificationData.unSeenCount += 1;
        }

        newList.unshift(newNotification);
      }

      newAdminChatNotificationData.notificationListData.list = newList;
    }

    state.adminChatNotificationData = { ...newAdminChatNotificationData };
  },
  onAdminChatSyncedMessagesReceived: (
    state: IInitialAdminChatState,
    action: PayloadAction<IMessage[]>
  ) => {
    const messages = action.payload || [];
    const firstMessage = messages[0];
    const firstMessageConversationId = firstMessage
      ? (firstMessage.conversation as string)
      : undefined;

    // Update messages on sync
    if (
      state.viewMode === ADMIN_CHAT_VIEW_MODES.CONVERSATION &&
      state.selectedConversation &&
      state.selectedConversation._id === firstMessageConversationId
    ) {
      const newAdminChatConversationMessagesData = {
        ...state.adminChatConversationMessagesData,
      };

      if (newAdminChatConversationMessagesData.listData) {
        const newListData = {
          ...newAdminChatConversationMessagesData.listData,
        };

        const newMessages = newListData.messages || [];

        messages.forEach((messageItem) => {
          const index = newMessages.findIndex(
            (item) => item._id === messageItem._id
          );

          if (index > -1) {
            newMessages[index] = { ...messageItem };
          }
        });

        newListData.messages = newMessages;

        state.adminChatConversationMessagesData = newAdminChatConversationMessagesData;
      }
    }

    // Update notification data & conversation data
    const newAdminChatNotificationData = { ...state.adminChatNotificationData };
    const newNotificationList =
      newAdminChatNotificationData.notificationListData &&
      newAdminChatNotificationData.notificationListData.list
        ? [...newAdminChatNotificationData.notificationListData.list]
        : [];

    const newAdminChatConversationsData = {
      ...state.adminChatConversationsData,
    };

    const newConversations = [...newAdminChatConversationsData.list];

    messages.forEach((messageItem) => {
      const statuses = messageItem.status;
      const conversationId = messageItem.conversation as string;

      const isSeen = statuses.some(
        (item) => item.status === 'SEEN' && item.user === state.authUserId
      );

      if (isSeen) {
        const _index = newNotificationList.findIndex(
          (item) => conversationId === item._id
        );

        if (_index > -1) {
          const _matchingItem: IChatNotification = {
            ...newNotificationList[_index],
          };

          // CAN BE CALLED TWICE CHECK
          if (
            _matchingItem.unseenCount &&
            newAdminChatNotificationData.unSeenCount
          ) {
            _matchingItem.unseenCount -= 1;

            newNotificationList[_index] = { ..._matchingItem };

            newAdminChatNotificationData.unSeenCount -= 1;
          }

          // Update conversation unseencount
          const conversationIndex = newConversations.findIndex(
            (item) => item._id === conversationId
          );

          if (conversationIndex > -1) {
            const _matchingConversation = {
              ...newConversations[conversationIndex],
            };

            if (_matchingConversation && _matchingConversation.unSeenCount) {
              _matchingConversation.unSeenCount -= 1;
            }

            newConversations[conversationIndex] = {
              ..._matchingConversation,
            };
          }
        }
      }
    });

    if (newAdminChatNotificationData.notificationListData) {
      newAdminChatNotificationData.notificationListData.list = [
        ...newNotificationList,
      ];
    }

    newAdminChatConversationsData.list = newConversations;

    state.adminChatNotificationData = { ...newAdminChatNotificationData };
    state.adminChatConversationsData = { ...newAdminChatConversationsData };
  },
  // Notifications
  setAdminChatUserNotificationsData: (
    state: IInitialAdminChatState,
    action: PayloadAction<{
      unSeenCount: 0;
      notificationListData: {
        limit?: number;
        offset?: number;
        totalDocs?: number;
        docs: Array<IChatNotification>;
      };
    }>
  ) => {
    const result = action.payload;

    state.adminChatNotificationData = {
      unSeenCount: result.unSeenCount,
      notificationListData: {
        totalDocs: result.notificationListData.totalDocs || 0,
        offset: result.notificationListData.offset || 0,
        limit: result.notificationListData.limit || 0,
        list: result.notificationListData.docs,
      },
    };
  },
  setAdminChatLoadMoreNotificationData: (
    state: IInitialAdminChatState,
    action: PayloadAction<{
      limit?: number;
      offset?: number;
      totalDocs?: number;
      docs: Array<IChatNotification>;
    }>
  ) => {
    const result = action.payload;

    const newAdminChatNotificationData = { ...state.adminChatNotificationData };

    if (newAdminChatNotificationData.notificationListData) {
      const newNotificationListData = {
        ...newAdminChatNotificationData.notificationListData,
      };

      newNotificationListData.limit = result.limit;
      newNotificationListData.offset = result.offset;
      newNotificationListData.list = [...newNotificationListData.list].concat(
        result.docs
      );

      newAdminChatNotificationData.notificationListData = newNotificationListData;
    }

    newAdminChatNotificationData.isLoadingMore = undefined;

    state.adminChatNotificationData = newAdminChatNotificationData;
  },
};

export const AdminChatSlice = createSlice({
  name: 'adminChat',
  initialState,
  reducers: {
    ...chatMiddlewareRequestActions,
    ...chatMiddlewareResultSetActions,
    // Others
    resetAdminChatMessages: (state) => {
      state.adminChatConversationMessagesData = {
        ...initialState.adminChatConversationMessagesData,
      };
    },
    setAdminChatViewMode: (
      state,
      action: PayloadAction<TAdminChatViewMode | undefined>
    ) => {
      state.viewMode = action.payload;
    },
    setAdminChatSelectedConversation: (
      state,
      action: PayloadAction<IConversation | undefined>
    ) => {
      state.selectedConversation = action.payload;
    },
    clearConversationNotificationData: (
      state,
      action: PayloadAction<string>
    ) => {
      const conversationId = action.payload;

      const newAdminChatConversationsData = {
        ...state.adminChatConversationsData,
      };

      const newConversations = [...(newAdminChatConversationsData.list || [])];

      // Update conversations data
      const conversationIndex = newConversations.findIndex(
        (item) => item._id === conversationId
      );

      if (conversationIndex > -1) {
        const matchingConversationItem = {
          ...newConversations[conversationIndex],
        };

        matchingConversationItem.unSeenCount = 0;

        newConversations[conversationIndex] = { ...matchingConversationItem };
      }

      newAdminChatConversationsData.list = newConversations;

      state.adminChatConversationsData = newAdminChatConversationsData;

      // Update notification data
      const newAdminChatNotificationData = {
        ...state.adminChatNotificationData,
      };

      if (newAdminChatNotificationData.notificationListData) {
        const newNotificationList = [
          ...newAdminChatNotificationData.notificationListData.list,
        ];

        const notificationIndex = newNotificationList.findIndex(
          (item) => item._id === conversationId
        );

        if (notificationIndex > -1) {
          const matchingNotificationItem = {
            ...newNotificationList[notificationIndex],
          };

          const countToBeDeducted = matchingNotificationItem.unseenCount || 0;

          matchingNotificationItem.unseenCount = 0;

          if (newAdminChatNotificationData.unSeenCount) {
            newAdminChatNotificationData.unSeenCount -= countToBeDeducted;

            if (newAdminChatNotificationData.unSeenCount < 0) {
              newAdminChatNotificationData.unSeenCount = 0;
            }
          }
        }

        newAdminChatNotificationData.notificationListData.list = newNotificationList;
      }

      state.adminChatNotificationData = newAdminChatNotificationData;
    },
    resetAdminCourseChatData: (state) => {
      state.courseId = undefined;
      state.viewMode = initialState.viewMode;
      state.selectedConversation = undefined;
      state.adminChatConversationsData = {
        ...initialState.adminChatConversationsData,
      };
      state.adminChatConversationMessagesData = {
        ...initialState.adminChatConversationMessagesData,
      };
    },
  },
  extraReducers(builder) {
    builder.addCase(getAuthUser.fulfilled, (state, action) => {
      const result = action.payload;

      if (result) {
        state.authUserId = result._id;
      }
    });
  },
});

export const {
  adminChatSetConnection,
  adminChatGetConversations,
  onAdminChatUserConnectionSynced,
  // Conversations
  setAdminChatConversations,
  adminChatStartNewConversation,
  setAdminChatNewConversation,
  // Messages
  adminChatGetConversationMessages,
  setAdminChatConversationMessages,
  onAdminChatConversationMessageReceived,
  adminChatSendConversationMessage,
  adminChatSyncConversationMessages,
  onAdminChatSyncedMessagesReceived,
  // Notification
  setAdminChatUserNotificationsData,
  adminChatLoadMoreNotificationData,
  setAdminChatLoadMoreNotificationData,
  // Others
  onAdminChatDisconnected,
  resetAdminChatMessages,
  setAdminChatViewMode,
  setAdminChatSelectedConversation,
  clearConversationNotificationData,
  resetAdminCourseChatData,
} = AdminChatSlice.actions;

export const getAdminChatState = (state: RootState): RootState['adminChat'] =>
  state.adminChat;

export default AdminChatSlice.reducer;
